/**
 * Reusable parallax point cloud layer.
 * Creates a field of colored points that offset based on camera position,
 * creating depth perception. Points wrap via modulo for infinite tiling.
 *
 * Uses custom ShaderMaterial with gl_PointCoord for circular soft-edge
 * rendering instead of the default points material (which only renders squares).
 * Minimum gl_PointSize clamped to 2.0px to prevent sub-pixel invisibility.
 */

import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { mulberry32 } from '@warmancer/shared';

export interface ParallaxStarfieldProps {
  /** Number of points in the field */
  readonly count: number;
  /** Parallax factor: 0 = infinitely far (no motion), 1 = same plane as camera */
  readonly parallaxFactor: number;
  /** Y depth below the game plane (negative = below) */
  readonly yDepth: number;
  /** [min, max] point size range */
  readonly sizeRange: [number, number];
  /** Array of hex color strings to randomly assign */
  readonly colors: readonly string[];
  /** Half-size of the point distribution area */
  readonly spread: number;
  /** Seed for deterministic PRNG (same seed = same layout for all players) */
  readonly seed: number;
  /** Per-particle drift speed range [min, max] units/sec (0 = no drift) */
  readonly driftSpeed?: [number, number];
  /** Render as irregular rocky chunks instead of soft glowing circles */
  readonly rocky?: boolean;
  /** Server time base in seconds for deterministic drift (serverTick * TICK_DELTA) */
  readonly serverTimeBase?: number;
}

const starVertexShader = /* glsl */ `
  attribute float size;
  varying vec3 vColor;

  void main() {
    vColor = color;
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    // Clamp minimum 2.0px to prevent sub-pixel invisibility on camera motion
    gl_PointSize = max(size * (50.0 / -mvPosition.z), 2.0);
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const starFragmentShader = /* glsl */ `
  varying vec3 vColor;

  void main() {
    float d = length(gl_PointCoord - vec2(0.5));
    if (d > 0.5) discard;
    // Soft circular fade: fully opaque center, smooth falloff to edge
    float alpha = smoothstep(0.5, 0.3, d);
    gl_FragColor = vec4(vColor, alpha);
  }
`;

// Rocky fragment shader — irregular jagged edges, opaque, no glow
const rockyFragmentShader = /* glsl */ `
  varying vec3 vColor;

  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
  }

  void main() {
    vec2 uv = gl_PointCoord - vec2(0.5);
    float d = length(uv);

    // Angular noise: chop the edge into 6 angular segments with random radii
    float angle = atan(uv.y, uv.x);
    float segment = floor(angle * 3.0 / 3.14159);
    float noise = hash(vec2(segment, floor(d * 8.0)));
    float edge = 0.28 + noise * 0.18;

    if (d > edge) discard;

    // Internal brightness variation for surface texture
    float grain = 0.75 + 0.25 * hash(floor(gl_PointCoord * 6.0));
    gl_FragColor = vec4(vColor * grain, 0.9);
  }
`;

export function ParallaxStarfield({
  count,
  parallaxFactor,
  yDepth,
  sizeRange,
  colors,
  spread,
  seed,
  driftSpeed,
  rocky,
  serverTimeBase,
}: ParallaxStarfieldProps) {
  const groupRef = useRef<THREE.Group>(null);
  const elapsedRef = useRef(0);
  const timeInitializedRef = useRef(false);

  // Destructure to primitive values for stable useMemo deps
  // (inline arrays like sizeRange={[1,2]} are new references every render)
  const sizeMin = sizeRange[0];
  const sizeMax = sizeRange[1];
  const driftMin = driftSpeed ? driftSpeed[0] : 0;
  const driftMax = driftSpeed ? driftSpeed[1] : 0;

  // Build geometry once: positions, colors, sizes, drift vectors, and base positions for drift layers
  const { positions, colorsArray, sizes, driftVectors, basePositions } = useMemo(() => {
    const rng = mulberry32(seed);
    const pos = new Float32Array(count * 3);
    const col = new Float32Array(count * 3);
    const sz = new Float32Array(count);
    // Per-particle drift: dx, dz velocities (units/sec)
    const drift = new Float32Array(count * 2);
    // Base positions for deterministic drift (position = base + drift * elapsed)
    const base = new Float32Array(count * 2);

    const colorObjects = colors.map((hex) => new THREE.Color(hex));

    for (let i = 0; i < count; i++) {
      // Distribute points in a large square centered at origin
      const bx = (rng() - 0.5) * spread * 2;
      const bz = (rng() - 0.5) * spread * 2;
      pos[i * 3] = bx;
      pos[i * 3 + 1] = 0; // flat in the layer plane
      pos[i * 3 + 2] = bz;
      base[i * 2] = bx;
      base[i * 2 + 1] = bz;

      // Random color from palette
      const color = colorObjects[Math.floor(rng() * colorObjects.length)];
      col[i * 3] = color.r;
      col[i * 3 + 1] = color.g;
      col[i * 3 + 2] = color.b;

      // Random size in range
      sz[i] = sizeMin + rng() * (sizeMax - sizeMin);

      // Drift: random direction + speed
      if (driftMin > 0 || driftMax > 0) {
        const angle = rng() * Math.PI * 2;
        const speed = driftMin + rng() * (driftMax - driftMin);
        drift[i * 2] = Math.cos(angle) * speed;
        drift[i * 2 + 1] = Math.sin(angle) * speed;
      }
    }

    return { positions: pos, colorsArray: col, sizes: sz, driftVectors: drift, basePositions: base };
  }, [count, colors, sizeMin, sizeMax, spread, seed, driftMin, driftMax]);

  // Custom shader material — rocky uses jagged edges + normal blending,
  // stars use soft circles + additive blending
  const shaderMaterial = useMemo(
    () =>
      new THREE.ShaderMaterial({
        vertexShader: starVertexShader,
        fragmentShader: rocky ? rockyFragmentShader : starFragmentShader,
        transparent: true,
        depthWrite: false,
        blending: rocky ? THREE.NormalBlending : THREE.AdditiveBlending,
        vertexColors: true,
      }),
    [rocky],
  );

  const hasDrift = driftMin > 0 || driftMax > 0;

  // Each frame: offset group position by camera * parallaxFactor,
  // compute per-particle drift from base positions + elapsed time, and wrap for infinite tiling
  useFrame((state, delta) => {
    if (!groupRef.current) return;

    // Sync elapsed time to server time base on first available tick
    if (!timeInitializedRef.current && serverTimeBase !== undefined && serverTimeBase > 0) {
      elapsedRef.current = serverTimeBase;
      timeInitializedRef.current = true;
    }
    // Cap delta to prevent tab-switch jumps
    elapsedRef.current += Math.min(delta, 0.1);

    const camX = state.camera.position.x;
    const camZ = state.camera.position.z;

    // Offset the group for parallax effect
    groupRef.current.position.x = camX * parallaxFactor;
    groupRef.current.position.z = camZ * parallaxFactor;

    // Wrap individual particles to keep them around the camera
    const geometry = groupRef.current.children[0] as THREE.Points | undefined;
    if (!geometry) return;
    const posAttr = (geometry.geometry as THREE.BufferGeometry).getAttribute('position') as THREE.BufferAttribute;
    if (!posAttr) return;

    const arr = posAttr.array as Float32Array;
    const wrapSize = spread;

    // Camera position in the layer's local space
    const localCamX = camX * (1 - parallaxFactor);
    const localCamZ = camZ * (1 - parallaxFactor);

    const elapsed = elapsedRef.current;

    let needsUpdate = hasDrift;
    for (let i = 0; i < count; i++) {
      const ix = i * 3;
      const iz = i * 3 + 2;

      // Compute drift position from base + driftVector * elapsed (deterministic, no frame-delta accumulation)
      if (hasDrift) {
        arr[ix] = basePositions[i * 2] + driftVectors[i * 2] * elapsed;
        arr[iz] = basePositions[i * 2 + 1] + driftVectors[i * 2 + 1] * elapsed;
      }

      // Distance from camera in local space
      const dx = arr[ix] - localCamX;
      const dz = arr[iz] - localCamZ;

      // Wrap if too far from camera
      if (dx > wrapSize) { arr[ix] -= wrapSize * 2; needsUpdate = true; }
      else if (dx < -wrapSize) { arr[ix] += wrapSize * 2; needsUpdate = true; }
      if (dz > wrapSize) { arr[iz] -= wrapSize * 2; needsUpdate = true; }
      else if (dz < -wrapSize) { arr[iz] += wrapSize * 2; needsUpdate = true; }
    }

    if (needsUpdate) {
      posAttr.needsUpdate = true;
    }
  });

  return (
    <group ref={groupRef} position={[0, yDepth, 0]}>
      <points material={shaderMaterial}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            args={[positions, 3]}
          />
          <bufferAttribute
            attach="attributes-color"
            args={[colorsArray, 3]}
          />
          <bufferAttribute
            attach="attributes-size"
            args={[sizes, 1]}
          />
        </bufferGeometry>
      </points>
    </group>
  );
}
